// Общая тема графиков отчётов модуля reports: типографика осей, сетка, легенда,
// тултип, скругления баров, семантическая палитра денег и светофор явки. Файл лежит на уровень
// выше папок конкретных отчётов (sovkombank/, будущие кубы данных) — вид графиков
// и денежные понятия во всех наборах дашбордов одинаковый, переиспользование —
// по плану редизайна, раздел 12.
// СИНХРОНИЗАЦИЯ: значения взяты из src/scss/_colors.scss — SCSS-переменные в TS не
// импортируются, поэтому дублируются здесь. Правишь _colors.scss — проверь эти константы.

// $gray-600 — подписи осей и легенды; тот же цвет у подписей осей тепловой карты
// (chartHeatmapAxisText в sovkombank/chartColors.ts).
const CHART_AXIS_FILL = '#6C757D';
// $gray-300 — линии сетки: мягче дефолтного серого Recharts (#ccc), сетка не спорит
// с данными.
const CHART_GRID_FILL = '#DEE2E6';

// Подписи тиков осей: 12px серым — «тихая» типографика, цифры рядов крупнее подписей
export const CHART_AXIS_TICK_PROPS = {
  fontSize: 12,
  fill: CHART_AXIS_FILL,
};

// Сетка: только горизонтальные линии пунктиром — вертикальные дублируют тики оси X
export const CHART_GRID_PROPS = {
  stroke: CHART_GRID_FILL,
  strokeDasharray: '3 3',
  vertical: false,
} as const;

// Скругление концов баров: вертикальных (верх) и горизонтальных (правый край)
export const CHART_BAR_RADIUS = [4, 4, 0, 0] as [number, number, number, number];
export const CHART_BAR_RADIUS_HORIZONTAL = [0, 4, 4, 0] as [number, number, number, number];

// Карточка тултипа — один вид во всех графиках отчётов (белая, лёгкая тень, small)
export const CHART_TOOLTIP_CLASS_NAME = 'bg-white border rounded p-2 small shadow-sm';

// Легенда: мелкие круглые маркеры вместо линий/квадратов по умолчанию
export const CHART_LEGEND_PROPS = {
  iconSize: 10,
  iconType: 'circle',
} as const;

// Отступы графика от краёв контейнера — единый ритм блоков
export const CHART_MARGIN_PROPS = {
  top: 10,
  right: 10,
  left: 0,
  bottom: 0,
} as const;

// ===== Клик по полю графика (onClick на самом BarChart/ComposedChart) =====
// Recharts разрешает активную строку/столбец по позиции указателя тем же
// механизмом, что и тултипу, и передаёт её индекс в data — activeTooltipIndex.
// Зона попадания становится всей полосой строки/столбца, а не только цветным
// прямоугольником бара: при крупном максимуме рядом столбик мелкого значения
// шириной 2–3 пикселя — по нему не попасть (баг корзин бонусов отчёта
// «Аналитика клиентов», 28.09). Подмножество MouseHandlerDataParam из recharts.
export interface ChartClickState {
  activeTooltipIndex?: number | string,
}

// Индекс элемента data из состояния клика; null — клик мимо данных (вне поля
// графика) или составной индекс мульти-серии (клик по решётке баров).
export const chartClickIndex = (state: ChartClickState): number | null => {
  const raw = state.activeTooltipIndex;
  if (raw == null || raw === '') { return null; }
  const index = Number(raw);
  return Number.isInteger(index) ? index : null;
};

// ===== Семантическая палитра денег (этап 2 редизайна, план, раздел 12) =====
// Правило: одно денежное понятие — один цвет и одна подпись во всех наборах
// дашбордов (сейчас Совкомбанк, дальше новые кубы данных). Графики, тултипы,
// таблицы и KPI берут цвет и подпись только отсюда — литеральные значения для
// этих понятий в компонентах запрещены.
// color — hex для SVG графиков (SCSS-переменные в TS не импортируются, см.
// СИНХРОНИЗАЦИЮ выше). HTML-текст (значения KPI, суммы в таблицах списков)
// стандарты красят только Bootstrap-утилитами — inline-стили и hex в
// компонентах запрещены, — поэтому у понятия есть и textClassName: тема того
// же смысла. Пары не совпадают тон в тон (зелёный $sumFontColor #2DA771 ↔
// text-success #28A745, фиолетовый $avtoappColorFont #722BF5 ↔ text-primary
// $primary #4A47A3): график и текст нигде не стоят рядом, а точнее стандарты
// не позволяют.
export const MONEY_SERIES = {
  // $sumFontColor — «денежный» зелёный проекта: деньги, фактически полученные.
  // «Поступления» = «Оплачено» + «Предоплаты», поэтому оба понятия зелёные: в одном
  // графике они не встречаются — «Динамика по периодам» рисует итог, «Финансовая
  // динамика» / дни недели / часы — только составляющие.
  moneyIn: { label: 'Поступления', color: '#2DA771', textClassName: 'text-success' },
  paid: { label: 'Оплачено', color: '#2DA771', textClassName: 'text-success' },
  // $avtoappColorFont — яркий фиолетовый. «Предоплаты» всегда рядом с «Оплачено»
  // (стек финдинамики, линии дней недели и часов), поэтому цвет обязан отличаться
  // и от зелёного, и от «неденежных» $primary / $hintColorNextField (записи, приехали).
  // До этапа 2 предоплаты были серой линией в днях недели/часах и бледно-фиолетовым
  // баром в финдинамике — эту нестыковку палитра устраняет. В тексте (textClassName)
  // ограничение слабее: таблицы не стоят рядом с графиками, поэтому фиолетовый
  // берём ближайшую разрешённую тему — text-primary ($primary).
  prepaid: { label: 'Предоплаты', color: '#722BF5', textClassName: 'text-primary' },
  // $deletedContentColor — тёплый лососёвый: деньги ещё не получены (дебиторка).
  // В отчёте Совкомбанка поле есть в данных, но пока не рисуется — цвет и подпись
  // зарезервированы для будущих кубов; при первом использовании в одном графике
  // с «Возвратами» ($danger) проверить различимость.
  unpaid: { label: 'Не оплачено', color: '#E88671' },
  // $danger — возврат полученных денег.
  refunded: { label: 'Возвраты', color: '#dc3545', textClassName: 'text-danger' },
  // Бонусы — «не совсем деньги»: серая пара (начислено темнее, списано светлее),
  // всегда пунктиром (CHART_BONUS_DASHARRAY). До этапа 2 «Начислено бонусов» делило
  // зелёный с поступлениями/оплачено и путалось с ними в легенде. $gray-600 / $gray-500.
  bonusReceived: { label: 'Начислено бонусов', color: '#6C757D' },
  bonusSpent: { label: 'Списано бонусов', color: '#ADB5BD' },
} as const;

// Пунктир бонусных серий — визуальный признак «не совсем деньги» во всех графиках
export const CHART_BONUS_DASHARRAY = '6 3';

// ===== Семантическая палитра записей (этап 5 редизайна, план, раздел 12) =====
// Как у денег: одно понятие — один цвет и одна подпись во всех наборах
// дашбордов. Триада «записей / приехали / явка» — базовые ряды любого куба по
// записям (разрезы, сводные блоки, воронка); цвета и подписи серий берутся
// только отсюда. sovkombank/chartColors доопределяет свои токены (chartPrimary
// и др.) от этой палитры — исторические потребители не меняются.
export const APPOINTMENT_SERIES = {
  // $primary — базовый цвет проекта: «Записей» — главный объём отчёта.
  records: { label: 'Записей', color: '#4A47A3' },
  // $hintColorNextField — приглушённый фиолетовый: «Приехали» — часть записей.
  arrived: { label: 'Приехали', color: '#AFA9EC' },
  // $sumFontColor — зелёный «успешного исхода»: линия «Явка». Тот же токен, что
  // у MONEY_SERIES.paid/moneyIn — «приехал и заплатил» исходы одной природы;
  // в одном графике эти серии не пересекаются (см. комментарий у moneyIn).
  attendance: { label: 'Явка', color: '#2DA771' },
} as const;

// ===== Светофор доли явки (этап 4 редизайна, план, раздел 12) =====
// Явка — ключевая метрика отчётов по записям, тон одинаков во всех блоках и
// будущих кубах: ≥80% — хорошо, 60–80% — требует внимания, <60% — плохо. Текст
// красится готовыми утилитами Bootstrap (text-success и т.п.), а для SVG (кольцо
// RadialBar в KPI) нужны те же темы в hex — текст и кольцо совпадают тон в тон.
// null («метрики нет»: прошедших визитов не было) — нейтральный серый: это не
// плохо и не хорошо.
// СИНХРОНИЗАЦИЯ: дефолтные $theme-colors Bootstrap 4.6 (node_modules/bootstrap/
// scss/_variables.scss) — проект их не переопределяет; danger совпадает с $danger
// из _colors.scss; нейтральный — $gray-500 из _colors.scss.
const RATE_TONE_NEUTRAL = { className: 'text-muted', color: '#ADB5BD' };

export const RATE_TONES = {
  good: { className: 'text-success', color: '#28A745' },
  attention: { className: 'text-warning', color: '#FFC107' },
  bad: { className: 'text-danger', color: '#DC3545' },
} as const;

// Доля явки — 0…1 (не проценты), как её считают агрегаты отчёта
export const getAttendanceRateTone = (rate: number | null): { className: string, color: string } => {
  if (rate === null) return RATE_TONE_NEUTRAL;
  if (rate >= 0.8) return RATE_TONES.good;
  if (rate >= 0.6) return RATE_TONES.attention;
  return RATE_TONES.bad;
};

// Дорожка кольца явки в KPI — $gray-300, как линии сетки: незакрытая доля не кричит
export const CHART_RING_TRACK_FILL = '#DEE2E6';
